// Share Creation (ESOP share detail) verification (legacy /AccountsApproval/VerifyShareCreation) —
// an employee's share grant: scheme, vesting, allocation, exercise option and compliance document.
// Verified/approved via the shared status list (legacy drops "Return" here).
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Award, Clock, ExternalLink } from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import ActionButtons from '../../components/Inbox/ActionButtons';
import RemarksHistory from '../../components/Inbox/RemarksHistory';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import { SimpleCommentInput } from '../../components/Inbox/VerificationInput';
import { buildShareCreationUrl } from '../../config/s3Config';

import {
    fetchShareCreationQueue,
    fetchShareCreationDetail,
    submitApproveShareCreation,
    clearShareCreationDetail,
    resetVerifyShareCreation,
    selectVSRQueue, selectVSRQueueLoading, selectVSRQueueError,
    selectVSRDetail, selectVSRDetailLoading, selectVSRDetailError,
    selectVSRVerifyLoading,
} from '../../slices/accountsSlice/verifyShareCreationSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton,
} from '../../slices/CommonSlice/getStatusSlice';

import {
    fetchRemarks,
    selectRemarks,
    selectRemarksLoading,
    setSelectedMOID,
} from '../../slices/supplierPOSlice/purcahseHelperSlice';

const ACTION_VERB = {
    Verify: 'Verified Successfully',
    Approve: 'Approved Successfully',
    Reject: 'Rejected Successfully',
};

// Section → [label, property] as legacy lays them out.
const SECTIONS = [
    ['Employee Details', [
        ['Employee Name', 'EmployeeName'], ['Employee ID', 'EmployeeId'], ['Department', 'Department'], ['Position', 'Position'],
        ['Employment Start Date', 'EmpStartDate'], ['Vesting Start Date', 'VestingStartDate'], ['Vesting End Date', 'VestingEndDate'],
        ['Vesting Percentage', 'VestingPercentage'], ['Exercise Price per Share', 'ExercisePricePerShare'],
    ]],
    ['Allocation Details', [
        ['Grant ID', 'GrantID'], ['Grant Date', 'GrantDate'], ['No. of Shares Granted', 'NumberofSharesGranted'], ['Grant Status', 'GrantStatus'],
    ]],
    ['Exercise Option', [
        ['Option ID', 'OptionID'], ['Exercise Date', 'ExerciseDate'], ['No. of Shares Exercised', 'NumberofSharesExercised'],
        ['Exercise Price', 'ExercisePrice'], ['Exercise Amount', 'ExerciseAmount'], ['Exercise Status', 'ExerciseStatus'],
    ]],
];

const Field = ({ label, value, className = '' }) => (
    <div className={className}>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 break-words">{value || value === 0 ? value : '—'}</p>
    </div>
);

const StatusBadge = () => (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 whitespace-nowrap">Verification Pending</span>
);

const VerifyShareCreation = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;

    const queue = useSelector(selectVSRQueue);
    const queueLoading = useSelector(selectVSRQueueLoading);
    const queueError = useSelector(selectVSRQueueError);
    const detail = useSelector(selectVSRDetail);
    const detailLoading = useSelector(selectVSRDetailLoading);
    const detailError = useSelector(selectVSRDetailError);
    const verifyLoading = useSelector(selectVSRVerifyLoading);

    const statusLoading = useSelector(selectStatusListLoading);
    const statusError = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions = useSelector(selectHasActions);

    const remarks = useSelector(selectRemarks);
    const remarksLoading = useSelector(selectRemarksLoading);

    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    const [selectedItem, setSelectedItem] = useState(null);
    const [comment, setComment] = useState('');
    const [showRemarksHistory, setShowRemarksHistory] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered, setIsLeftPanelHovered] = useState(false);

    const getCurrentUser = () => userData?.userName || userDetails?.userName || 'system';

    // Only trust the detail that belongs to the row currently selected.
    const share = selectedItem && String(detail?.RefNo) === String(selectedItem.RefNo) ? detail : null;

    useEffect(() => {
        if (roleId) dispatch(fetchShareCreationQueue(roleId));
    }, [roleId, dispatch]);

    useEffect(() => {
        // Legacy removes "Return" from this page's status dropdown.
        dispatch(setShowReturnButton('No'));
        return () => {
            dispatch(resetVerifyShareCreation());
            dispatch(resetApprovalData());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!selectedItem) return;
        dispatch(clearShareCreationDetail());
        dispatch(resetApprovalData());
        dispatch(setShowReturnButton('No'));
        dispatch(fetchShareCreationDetail(selectedItem.RefNo));
        setComment('');
        setShowRemarksHistory(false);
        setIsLeftPanelCollapsed(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem]);

    // MOID comes with the detail. Remarks are keyed on the ref no.
    useEffect(() => {
        if (!share?.MOID || !roleId) return;
        dispatch(fetchStatusList({ MOID: share.MOID, ROID: roleId, ChkAmt: 0 }));
        dispatch(setSelectedMOID(share.MOID));
        dispatch(fetchRemarks({ trno: share.RefNo, moid: share.MOID }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [share?.MOID, share?.RefNo]);

    const handleBackToInbox = () => {
        if (onNavigate) onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
    };

    const handleRefresh = () => {
        if (roleId) dispatch(fetchShareCreationQueue(roleId));
    };

    const finishAndRefresh = () => {
        setTimeout(() => {
            dispatch(fetchShareCreationQueue(roleId));
            setSelectedItem(null);
            setComment('');
            setShowRemarksHistory(false);
            setIsLeftPanelCollapsed(false);
            dispatch(clearShareCreationDetail());
            dispatch(resetApprovalData());
        }, 800);
    };

    const handleActionClick = async (action) => {
        if (!share) { toast.error('No share detail selected'); return; }
        if (!comment.trim()) { toast.error('Enter Note'); return; }

        const actionValue = action.type;
        // spApproveShareCreation reads the action from Status and the note from Remarks.
        const payload = {
            RefNo: share.RefNo,
            Status: actionValue,
            Remarks: comment.trim(),
            RoleId: roleId,
            CreatedBy: getCurrentUser(),
        };

        try {
            const result = await dispatch(submitApproveShareCreation(payload)).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            if (status === 'Submitted' || status === 'Submited') {
                toast.success(`Share Details ${ACTION_VERB[actionValue] || `${actionValue}d`}`);
                finishAndRefresh();
                return;
            }
            toast.error(status || 'Error Occurred While Verification');
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error Occurred While Verification');
        }
    };

    const handleViewDocument = () => {
        const url = buildShareCreationUrl(share?.ComplianceUploadDocs);
        if (!url) { toast.error('No uploaded document available'); return; }
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const filteredQueue = queue.filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return String(item.RefNo ?? '').toLowerCase().includes(q)
            || item.FirstName?.toLowerCase().includes(q)
            || String(item.EmpRefNo ?? '').toLowerCase().includes(q)
            || item.Date?.toLowerCase().includes(q);
    });

    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 rounded-full border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
                    <Award className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">{item.FirstName}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.EmpRefNo} · Ref {item.RefNo}</p>
                </div>
            </div>
            <div className="flex items-center justify-between gap-2 text-xs">
                <span className="text-gray-500 dark:text-gray-400">{item.Date}</span>
                <StatusBadge />
            </div>
        </div>
    );

    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">{item.RefNo}</span>
            <span className="font-semibold text-gray-900 dark:text-white min-w-[180px] truncate">{item.FirstName}</span>
            <span className="text-gray-500 dark:text-gray-400 min-w-[100px]">{item.EmpRefNo}</span>
            <span className="text-gray-500 dark:text-gray-400 min-w-[100px]">{item.Date}</span>
            <span className="ml-auto"><StatusBadge /></span>
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
            <Award className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
    );

    const renderDetails = () => (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-gray-200 dark:border-gray-700 space-y-5">
            {SECTIONS.map(([title, fields], idx) => (
                <div key={title} className={idx ? 'pt-4 border-t border-gray-200 dark:border-gray-700' : ''}>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">{title}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {fields.map(([label, prop]) => <Field key={prop} label={label} value={share[prop]} />)}
                    </div>
                </div>
            ))}
            {share.ComplianceUploadDocs && (
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Compliance Upload</p>
                    <button type="button" onClick={handleViewDocument}
                        className="inline-flex items-center gap-1.5 text-sm font-bold text-red-800 dark:text-red-300 underline hover:no-underline">
                        <ExternalLink className="w-4 h-4" /> View Uploaded Docs
                    </button>
                </div>
            )}
        </div>
    );

    const renderActions = () => {
        if (statusLoading) {
            return (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                        <span className="text-gray-600 dark:text-gray-400">Loading actions…</span>
                    </div>
                </div>
            );
        }
        if (statusError) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ Error loading actions: {statusError}</p>
                </div>
            );
        }
        if (!hasActions || !enabledActions || enabledActions.length === 0) {
            return (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 border border-yellow-200 dark:border-yellow-700">
                    <p className="text-yellow-700 dark:text-yellow-400 text-center">ℹ️ No actions available for these share details</p>
                </div>
            );
        }
        return (
            <ActionButtons
                actions={enabledActions}
                onActionClick={handleActionClick}
                loading={verifyLoading}
                isVerified={true}
                comment={comment}
                showValidation={true}
            />
        );
    };

    const renderDetailContent = () => {
        if (!selectedItem) return null;

        if (detailLoading || (!share && !detailError)) {
            return (
                <div className="flex items-center justify-center gap-3 py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Loading share details…</span>
                </div>
            );
        }
        if (detailError || !share) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ {detailError || 'Share details could not be loaded — it may no longer be pending at your level.'}</p>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-6 border-2 border-indigo-200 dark:border-indigo-700">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{share.EmployeeName || selectedItem.FirstName}</h2>
                    <p className="text-indigo-600 dark:text-indigo-400 font-semibold mb-3">Ref {share.RefNo}</p>
                    <div className="flex flex-wrap gap-2">
                        {share.SchemeType && (
                            <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium">Scheme: {share.SchemeType}</span>
                        )}
                        <StatusBadge />
                    </div>
                </div>

                {renderDetails()}

                <RemarksHistory
                    isOpen={showRemarksHistory}
                    onToggle={() => setShowRemarksHistory(!showRemarksHistory)}
                    remarks={remarks}
                    loading={remarksLoading}
                    title="Approval History"
                />

                <SimpleCommentInput
                    comment={comment}
                    onCommentChange={(e) => setComment(e.target.value)}
                    label="Notes"
                    placeholder="Enter verification notes…"
                    required
                    rows={3}
                    showCharCount
                />
                {renderActions()}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || 'Share Detail Verification'} (${queue.length})`}
                subtitle={ModuleDisplayName}
                itemCount={queue.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={Award}
                badgeText="Share Detail Verification"
                badgeCount={queue.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by ref no, employee, date…',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredQueue,
                    selectedItem,
                    onItemSelect: setSelectedItem,
                    renderItem: renderItemCard,
                    renderListItem,
                    renderCollapsedItem,
                    loading: queueLoading,
                    error: queueError,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending',
                        icon: Clock,
                        emptyMessage: 'No share details pending verification!',
                        itemKey: 'RefNo',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                    },
                    renderPopupContent: () => renderDetailContent(),
                    popupConfig: {
                        title: 'Share Detail Verification',
                        icon: Award,
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem,
                    loading: false,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'Share Details',
                        icon: Award,
                        selectedTitle: 'Share Detail Verification',
                        emptyTitle: 'No Share Detail Selected',
                        emptyMessage: 'Select an employee share grant from the list to view details and take action.',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxHeight: 'calc(100vh-200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />
        </div>
    );
};

export default VerifyShareCreation;
